/**
 * useCockpitData -- Data hook voor de Cockpit Home pagina.
 * Haalt taken, projecten, meetings, deadlines en feedback op via bestaande endpoints.
 * Berekent urgente items en vandaag-specifieke data.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { formatDateISO, getMonday } from '../../utils/weekUtils';
import { isDagstaatTaak } from '../../utils/taakStatus';

// -- Date helpers (from RoleDashboard pattern) --------------------------------
function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
}

function isThisWeek(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const startOfWeek = getMonday(new Date()); // FE-08: op zondag sprong dit naar de volgende week
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  return d >= startOfWeek && d <= endOfWeek;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

function isOverdue(dateStr) {
  const days = daysUntil(dateStr);
  return days !== null && days < 0;
}

export default function useCockpitData(userEmail, userName) {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [draaidagen, setDraaidagen] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [userVoornaam, setUserVoornaam] = useState('');
  const [userFunctions, setUserFunctions] = useState([]);

  const fetchData = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);

    const today = formatDateISO(new Date());
    const twoWeeksOut = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];

    // Fire ALL fetches simultaneously -- each updates UI as soon as it resolves.
    const fetches = [
      authFetch(`${BACKEND_URL}/Api/personen?email=${encodeURIComponent(userEmail)}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const person = (data?.people || []).find(p => p.email?.toLowerCase() === userEmail.toLowerCase());
          if (person) {
            setUserVoornaam(person.voornaam || person.name?.split(' ')[0] || '');
            // Extract functions for work-audience resolution
            const funcs = person.functies || person.Functies || person.functions || [];
            setUserFunctions(Array.isArray(funcs) ? funcs : typeof funcs === 'string' ? funcs.split(', ') : []);
          }
          setLoading(false);
        }),
      authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`)
        .then(r => r.ok ? r.json() : {})
        .then(data => setTasks(data.tasks || [])),
      authFetch(`${BACKEND_URL}/Api/projects`)
        .then(r => r.ok ? r.json() : {})
        .then(data => setProjects(data.projects || [])),
      authFetch(`${BACKEND_URL}/Api/feedback/my?limit=10`)
        .then(r => r.ok ? r.json() : [])
        .then(data => setFeedback(Array.isArray(data) ? data : [])),
      authFetch(`${AI_ENGINE_URL}/api/v1/meetings/?user_email=${encodeURIComponent(userEmail)}&limit=5`)
        .then(r => r.ok ? r.json() : [])
        .then(data => setMeetings(data)),
      authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}&startDate=${today}&endDate=${twoWeeksOut}`)
        .then(r => r.ok ? r.json() : {})
        .then(data => setDeadlines(data.deadlines || data || [])),
      authFetch(`${BACKEND_URL}/Api/draaidagen/upcoming?weeks=2`)
        .then(r => r.ok ? r.json() : {})
        .then(data => setDraaidagen(data.draaidagen || data || [])),
    ];

    // Wait for all to settle (don't fail on individual errors)
    await Promise.allSettled(fetches.map(f => f.catch(err => console.warn('[CockpitData]', err.message))));
    setLoading(false);
  }, [userEmail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // -- Computed data -----------------------------------------------------------

  const voornaam = userVoornaam || userName?.split(' ')[0] || '';

  const myTasks = useMemo(() => {
    if (!voornaam) return tasks.map(t => ({ ...t, f: t.fields || {} }));
    return tasks
      .map(t => ({ ...t, f: t.fields || {} }))
      .filter(t => {
        const assignees = t.f['Voornaam Assignee'] || t.f['Email (Assignee)'] || '';
        return (Array.isArray(assignees) ? assignees : [assignees])
          .some(a => a?.toLowerCase()?.includes(voornaam.toLowerCase()) || a?.toLowerCase()?.includes(userEmail.toLowerCase()));
      });
  }, [tasks, voornaam, userEmail]);

  const activeTasks = useMemo(() =>
    myTasks.filter(t => !t.f.Afgerond && t.f.PrioNr !== 'Opgestuurd' && t.f.PrioNr !== 'Gearchiveerd'),
  [myTasks]);

  const todayTasks = useMemo(() =>
    activeTasks.filter(t => isToday(t.f.StartDateUitvoerder) || isToday(t.f.Deadline)),
  [activeTasks]);

  const weekTasks = useMemo(() =>
    activeTasks.filter(t => isThisWeek(t.f.StartDateUitvoerder) && !isToday(t.f.StartDateUitvoerder)),
  [activeTasks]);

  const overdueTasks = useMemo(() =>
    // Draaidag of vrije dag: voorbij is voorbij, nooit achterstallig (Joris, 3 nov 2026).
    activeTasks.filter(t => t.f.Deadline && isOverdue(t.f.Deadline) && !isDagstaatTaak(t.f)),
  [activeTasks]);

  const urgentDeadlines = useMemo(() => {
    if (!Array.isArray(deadlines)) return [];
    return deadlines.filter(d => {
      const days = daysUntil(d.deadline || d.Deadline);
      return days !== null && days >= 0 && days <= 7;
    }).slice(0, 6);
  }, [deadlines]);

  const activeProjects = useMemo(() =>
    (projects || []).filter(p => {
      const status = p.fields?.Status || p.status;
      return status === 'In progress' || status === 'In Progress';
    }).slice(0, 6),
  [projects]);

  const meetingsToProcess = useMemo(() =>
    (Array.isArray(meetings) ? meetings : meetings?.meetings || [])
      .filter(m => m.status === 'new' || m.status === 'review_needed')
      .slice(0, 5),
  [meetings]);

  const activeFeedback = useMemo(() =>
    feedback.filter(f => !['done', 'rejected', 'merged_to_main'].includes(f.pipeline_status)),
  [feedback]);

  // Attention counts
  const attention = useMemo(() => ({
    overdue: overdueTasks.length,
    todayTasks: todayTasks.length,
    meetings: meetingsToProcess.length,
    feedback: activeFeedback.filter(f => f.pipeline_status === 'awaiting_review').length,
    total: overdueTasks.length + meetingsToProcess.length + activeFeedback.filter(f => f.pipeline_status === 'awaiting_review').length,
  }), [overdueTasks, todayTasks, meetingsToProcess, activeFeedback]);

  return {
    loading,
    voornaam,
    userFunctions,
    // Raw
    tasks: myTasks,
    projects,
    meetings: meetingsToProcess,
    deadlines: urgentDeadlines,
    draaidagen,
    feedback: activeFeedback,
    activeProjects,
    // Computed
    todayTasks,
    weekTasks,
    overdueTasks,
    activeTasks,
    attention,
    // Actions
    refresh: fetchData,
  };
}
